import React from 'react';
import { toast } from 'sonner';
import { Badge, Btn, Card, Overline } from '../ForBrands/ui/BrandUI';
import { Icon } from '../ForBrands/ui/icons';
import '../ForBrands/brandMode.css';

// Partner API documentation: the single source of truth, rendered both on
// the admin API Keys page (API documentation tab) and on the public
// /p/api-docs route so API consumers can read it without an admin login.
// Docs always show the production base so snippets can be handed straight
// to partners. V2 styling only: bm tokens, BrandUI primitives and the V2
// stroke icons, so both routes read the same.
const PROD_BASE = 'https://app.cachescore.com';

// When the text below last changed, shown as "Last updated" at the top of
// the docs (notes 10/21 D10). Move it with every edit of the docs text.
export const DOCS_UPDATED = { iso: '2026-10-21', label: 'October 21, 2026' };

const copyText = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied to clipboard.`);
};

/* ------------------------------ shared bits ------------------------------ */

// Icon-only copy control for URLs and formats on a light surface.
const CopyButton = ({ text, label, ariaLabel }) => (
  <Btn
    variant="ghost"
    size="sm"
    className="!h-7 w-7 shrink-0 !px-0 text-bm-muted hover:text-bm-ink"
    onClick={() => copyText(text, label)}
    aria-label={ariaLabel}
    title={ariaLabel}
  >
    <Icon name="copy" size={14} />
  </Btn>
);

// A dark bm-ink panel with a mono snippet and a copy control.
const CodeBlock = ({ code, label = 'Snippet', language = '' }) => (
  <div className="min-w-0 overflow-hidden rounded-[12px] bg-bm-ink text-white">
    <div className="flex items-center justify-between gap-3 border-b border-white/10 py-2 pl-4 pr-2">
      <span className="bm-label-xs uppercase tracking-[0.06em] text-white/60">{language || label}</span>
      <Btn
        variant="ghost"
        size="sm"
        className="!text-white/80 hover:!bg-white/10 hover:!text-white"
        onClick={() => copyText(code, label)}
        aria-label={`Copy ${label}`}
      >
        <Icon name="copy" size={12} className="mr-1.5" /> Copy
      </Btn>
    </div>
    <pre className="overflow-x-auto whitespace-pre p-4 font-mono text-xs leading-relaxed text-white">
      {code}
    </pre>
  </div>
);

const MethodBadge = () => <Badge tone="dark">GET</Badge>;

const InlineCode = ({ children }) => (
  <code className="rounded-[4px] bg-bm-mutedbg px-1.5 py-0.5 font-mono text-[12px] text-bm-ink">
    {children}
  </code>
);

const SectionTitle = ({ overline, children }) => (
  <div className="mb-3 flex flex-col gap-[6px]">
    <Overline>{overline}</Overline>
    <h2 className="bm-h-m text-bm-ink">{children}</h2>
  </div>
);

const SubLabel = ({ children }) => <Overline className="mb-2">{children}</Overline>;

// A note under a section: the V2 subtle box, not a tinted callout.
const Note = ({ children }) => (
  <div className="mt-3 max-w-3xl rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-3 bm-body-m text-bm-muted">
    {children}
  </div>
);

// A labelled value tile: an overline, then a mono value with a copy control.
const ValueTile = ({ label, icon, children }) => (
  <Card padding="px-4 py-3" className="min-w-0">
    <div className="mb-1 flex items-center gap-1.5 text-bm-muted">
      {icon && <Icon name={icon} size={12} />}
      <Overline>{label}</Overline>
    </div>
    {children}
  </Card>
);

// The first column is code (a field, a status, a header) in mono, or, with
// `labelFirst`, a name people read (a plan, a field group) in the label style.
const DocsTable = ({ headers, rows, labelFirst = false }) => (
  <Card padding="p-0" className="w-full min-w-0 max-w-full overflow-x-auto">
    <table className="w-full text-left">
      <thead className="bg-bm-subtle">
        <tr>
          {headers.map((h) => (
            <th key={h} className="whitespace-nowrap border-b border-bm-border px-4 py-2.5 bm-label-xs uppercase tracking-[0.06em] text-bm-muted">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-bm-border align-top last:border-0">
            {row.map((cell, j) => (
              <td
                key={j}
                className={`px-4 py-2.5 ${j > 0 ? 'bm-body-m text-bm-muted' : labelFirst ? 'whitespace-nowrap bm-label-m text-bm-ink' : 'whitespace-nowrap font-mono text-xs text-bm-ink'}`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

// One endpoint: a header row (method, path, copy, summary) over its body.
const Endpoint = ({ path, copyUrl, summary, className = '', children }) => (
  <Card padding="p-0" className={`overflow-hidden ${className}`}>
    <div className="flex flex-wrap items-center gap-3 border-b border-bm-border px-5 py-4">
      <MethodBadge />
      <code className="min-w-0 break-all font-mono text-sm font-semibold text-bm-ink">{path}</code>
      <CopyButton text={copyUrl} label="Endpoint URL" ariaLabel="Copy URL" />
      <span className="bm-body-m text-bm-muted">{summary}</span>
    </div>
    <div className="space-y-4 px-5 py-4">{children}</div>
  </Card>
);

/* --------------------------- plans and credits --------------------------- */

const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');

// A field group as the docs name it, never its id: the server's label
// ("Performance"; an older catalog's "Performance: CacheScore, ..." reads up
// to the colon), else the id. What it holds: its description, else what an
// older label says after the colon.
const groupName = (group) => String(group?.label || group?.id || '').split(':')[0].trim();
const groupHolds = (group) => {
  if (group?.description) return String(group.description);
  const label = String(group?.label || '');
  return label.includes(':') ? label.slice(label.indexOf(':') + 1).trim() : '';
};

// The field groups the docs list: those with fields a response carries
// today. A group no partner response fills yet is left out, here and in the
// plans' group lists, so the docs never sell an empty group.
const shownGroups = (docs) =>
  (Array.isArray(docs?.fieldGroups) ? docs.fieldGroups : []).filter((g) => g && Array.isArray(g.fields) && g.fields.length > 0);

// A plan's field groups by name, in the plan's order, without the groups
// the docs leave out; the plan's own ids when the catalog sends no groups.
const planGroups = (plan, docs) => {
  const ids = Array.isArray(plan?.fieldGroups) ? plan.fieldGroups : [];
  if (!Array.isArray(docs?.fieldGroups)) return ids.join(', ');
  const byId = new Map(shownGroups(docs).map((g) => [g.id, g]));
  return ids.filter((id) => byId.has(id)).map((id) => groupName(byId.get(id))).join(', ');
};

// Ping names a key's field groups by id while the tables name them, so the
// ping example gets the ids beside their names, from the catalog (none
// without it). A group the docs leave out (no response fills it yet) is
// "any other id".
const PingGroupIds = ({ docs }) => {
  const groups = shownGroups(docs).filter((g) => g.id);
  if (!groups.length) return null;
  return (
    <p className="bm-body-m text-bm-muted">
      <InlineCode>fieldGroups</InlineCode> lists the key's field groups by id.
      The ids stand for the groups in Plans and credits:{' '}
      {groups.map((g, i) => (
        <React.Fragment key={g.id}>
          {i > 0 && (i === groups.length - 1 ? ' and ' : ', ')}
          <InlineCode>{g.id}</InlineCode> ({groupName(g)})
        </React.Fragment>
      ))}
      . Any other id adds no fields yet.
    </p>
  );
};

// The response headers the server documents ({name, description}).
const docsHeaders = (docs) => (Array.isArray(docs?.headers) ? docs.headers : []).filter((h) => h && h.name);

// "2026-10-21" as "October 21, 2026"; null for anything else.
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const dateLabel = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m || Number(m[2]) < 1 || Number(m[2]) > 12 || Number(m[3]) < 1 || Number(m[3]) > 31) return null;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
};

// When the docs last changed: this text's date (DOCS_UPDATED) or the
// server's plan catalog's (lastUpdated), whichever is later.
export const lastUpdatedOf = (docs) => {
  const server = docs?.lastUpdated;
  const iso = dateLabel(server) && server > DOCS_UPDATED.iso ? server : DOCS_UPDATED.iso;
  return { iso, label: dateLabel(iso) };
};

// The plans, prices, field groups, credit costs, packages and the 402 body,
// as the server sends them (users/api_plans.py PARTNER_PLAN_DOCS, the one
// place they live). Without them the section shows its intro only.
const PlansSection = ({ docs }) => {
  const groups = shownGroups(docs);
  const holds = groups.map(groupHolds);
  // The "What it holds" column only when a group says what it holds.
  const withHolds = holds.some(Boolean);
  return (
    <section id="plans">
      <SectionTitle overline="Pricing">Plans and credits</SectionTitle>
      <p className="mb-4 max-w-3xl bm-body-l text-bm-muted">
        Every key is on a plan. The plan decides which fields each response
        carries and how many credits the key gets every month. The Cache team
        sends a payment link for a plan or a credit package. Ping shows a key's
        plan, the credits it has left, and the field groups it unlocks.
      </p>
      {docs && (
        <div className="space-y-4">
          <DocsTable
            labelFirst
            headers={['Plan', 'Price', 'Monthly credits', 'Field groups']}
            rows={(docs.plans || []).map((p) => [
              p.label,
              p.priceMonthlyUsd ? `$${fmtNum(p.priceMonthlyUsd)} a month` : 'By agreement',
              p.monthlyCredits ? fmtNum(p.monthlyCredits) : 'No limit',
              planGroups(p, docs),
            ])}
          />
          {groups.length > 0 && (
            <DocsTable
              labelFirst
              headers={withHolds ? ['Field group', 'What it holds', 'Fields'] : ['Field group', 'Fields']}
              rows={groups.map((g, i) => (withHolds ? [groupName(g), holds[i], g.fields.join(', ')] : [groupName(g), g.fields.join(', ')]))}
            />
          )}
          <div className="grid gap-4 lg:grid-cols-2">
            <DocsTable
              headers={['Request', 'Credits']}
              rows={(docs.creditCosts || []).map((c) => [
                `GET ${c.endpoint}`,
                c.credits ? `${fmtNum(c.credits)} per request` : 'Free',
              ])}
            />
            <DocsTable
              headers={['Credit package', 'Price']}
              rows={(docs.packages || []).map((pk) => [`${fmtNum(pk.credits)} credits`, `$${fmtNum(pk.priceUsd)}`])}
            />
          </div>
          <ul className="max-w-3xl list-disc space-y-1 pl-5 bm-body-m text-bm-muted">
            {(docs.rules || []).map((rule) => <li key={rule}>{rule}</li>)}
          </ul>
          <Note>
            Billable responses of plan keys carry{' '}
            <InlineCode>X-Credits-Remaining</InlineCode>, the credits the key can
            still use.
            {docs.outOfCredits && (
              <>
                {' '}A plan key without enough credits left for a request gets{' '}
                <InlineCode>402</InlineCode> with this body:
              </>
            )}
          </Note>
          {docs.outOfCredits && (
            <CodeBlock
              language="402 response"
              label="Out of credits response"
              code={JSON.stringify(docs.outOfCredits.body, null, 2)}
            />
          )}
        </div>
      )}
    </section>
  );
};

/* --------------------------------- docs ---------------------------------- */

// `plans`: the Partner API plans the page loaded (the admin page passes
// its key list's catalog, the public page loads public/api-plans/).
// The examples are what the endpoints send today: a Full access key's
// answers (every field), then the same athlete for a Performance plan key.
const PartnerApiDocs = ({ plans = null }) => {
  const listExample = `{
  "athletes": [
    {
      "athleteId": "665f1c2e9b1d4a0012345678",
      "name": "Jordan",
      "surname": "Smith",
      "avatar": "https://res.cloudinary.com/.../avatars/xyz.jpg",
      "sport": "Basketball",
      "level": "High School",
      "team": "Oakland Soldiers 16U",
      "school": "Oakland High",
      "state": "California",
      "hometown": "Oakland, CA",
      "jerseyNumber": "23",
      "cacheScore": 74,
      "cacheScoreConfidence": "High",
      "performanceIndex": 7.8,
      "visibilityIndex": 6.1,
      "offCourtIndex": 5.5,
      "profileUrl": "${PROD_BASE}/p/665f1c2e9b1d4a0012345678",
      "cacheValue": { "min": 45000, "max": 61000 },
      "performanceValue": { "min": 31000, "max": 58000, "value": 47000 },
      "cacheNilxValue": { "min": 5600, "max": 7500, "tier": "Emerging" },
      "nilRateCard": {
        "events": null, "socialPost": 205, "reel": 305,
        "story": 90, "longVideo": null, "share": null
      },
      "brandAlignment": [
        { "category": "Athletic footwear", "fit": "Good" },
        { "category": "Sports nutrition", "fit": "Good" },
        { "category": "Consumer tech", "fit": "Moderate" }
      ]
    }
  ],
  "total": 128,
  "page": 1,
  "limit": 24,
  "sports": ["All Players", "Basketball", "Football", "Soccer"],
  "filterOptions": {
    "gender": [{ "value": 1, "label": "Male" }],
    "state": [{ "value": 5, "label": "California" }]
  }
}`;

  // The identity block every detail answer starts with (any plan).
  const detailIdentity = `  "athleteId": "665f1c2e9b1d4a0012345678",
  "name": "Jordan",
  "surname": "Smith",
  "avatar": "https://res.cloudinary.com/.../avatars/xyz.jpg",
  "sport": "Basketball",
  "level": "High School",
  "team": "Oakland Soldiers 16U",
  "school": "Oakland High",
  "college": "",
  "university": "",
  "highSchool": "Oakland High",
  "club": "Oakland Soldiers 16U",
  "proTeam": "",
  "collegeLevel": null,
  "position": "Point Guard",
  "jerseyNumber": "23",
  "location": "Oakland, CA",
  "gradYear": "2027",
  "privacy": {
    "cacheScorePublic": true,
    "cacheValueVisible": false,
    "nilDealsVisible": false
  },`;

  const detailExample = `{
${detailIdentity}
  "cacheScore": 74,
  "cacheScoreConfidence": "High",
  "contributions": { "performance": 78, "visibility": 61, "offCourt": 55 },
  "performanceIndex": 7.8,
  "visibilityIndex": 6.1,
  "offCourtIndex": 5.5,
  "cacheValue": { "min": 45000, "max": 61000 },
  "performanceValue": { "min": 31000, "max": 58000, "value": 47000 },
  "cacheNilxValue": { "min": 5600, "max": 7500, "tier": "Emerging" },
  "nilRateCard": {
    "events": null, "socialPost": 205, "reel": 305,
    "story": 90, "longVideo": null, "share": null
  },
  "brandAlignment": [
    { "category": "Athletic footwear", "fit": "Good" },
    { "category": "Sports nutrition", "fit": "Good" },
    { "category": "Consumer tech", "fit": "Moderate" }
  ],
  "profileUrl": "${PROD_BASE}/p/665f1c2e9b1d4a0012345678",
  "shareImageUrl": "${PROD_BASE}/api/users/public/athlete/665f1c2e9b1d4a0012345678/preview.png"
}`;

  // The same athlete for a Performance plan key: the Visibility and value
  // fields are filtered out, and the answer carries the credits left.
  const performancePlanExample = `HTTP/1.1 200 OK
Content-Type: application/json
X-Credits-Remaining: 1439

{
${detailIdentity}
  "cacheScore": 74,
  "cacheScoreConfidence": "High",
  "performanceIndex": 7.8,
  "performanceValue": { "min": 31000, "max": 58000, "value": 47000 },
  "profileUrl": "${PROD_BASE}/p/665f1c2e9b1d4a0012345678",
  "shareImageUrl": "${PROD_BASE}/api/users/public/athlete/665f1c2e9b1d4a0012345678/preview.png"
}`;

  // A merged athlete's old id (users/views.py _merged_redirect).
  const mergedExample = `HTTP/1.1 301 Moved Permanently
Location: /api/partner/v1/athletes/665f1c2e9b1d4a0087654321/
Cache-Control: no-store

{
  "mergedInto": "665f1c2e9b1d4a0087654321",
  "athleteId": "665f1c2e9b1d4a0087654321",
  "redirect": "/p/665f1c2e9b1d4a0087654321"
}`;

  const jsExample = `const res = await fetch(
  '${PROD_BASE}/api/partner/v1/athletes/?sport=Basketball&page=1&limit=24',
  { headers: { 'X-API-Key': 'cache_XXXXXXXXXXXX' } }
);
const data = await res.json();

// Embed a card, then link it to the Cache profile:
// <a href={athlete.profileUrl}>
//   <img src={athlete.avatar} /> {athlete.name} {athlete.surname}
//   CacheScore: {athlete.cacheScore}
// </a>`;

  const pyExample = `import requests

resp = requests.get(
    "${PROD_BASE}/api/partner/v1/athletes/",
    headers={"X-API-Key": "cache_XXXXXXXXXXXX"},
    params={"search": "jordan", "page": 1, "limit": 24},
    timeout=10,
)
resp.raise_for_status()
for athlete in resp.json()["athletes"]:
    print(athlete["name"], athlete.get("cacheScore"), athlete["profileUrl"])`;

  const searchFieldsExample = `curl -G "${PROD_BASE}/api/partner/v1/athletes/" \\
  -H "X-API-Key: cache_XXXXXXXXXXXX" \\
  --data-urlencode 'searchFields={"state":5,"cacheScore":60}' \\
  --data-urlencode 'sport=Basketball'`;

  const updated = lastUpdatedOf(plans);
  const headers = docsHeaders(plans);

  return (
    <div className="space-y-10">
      {/* Overview */}
      <section id="overview">
        <SectionTitle overline="Start here">Overview</SectionTitle>
        <p className="mb-3 bm-body-s text-bm-muted">
          Last updated <time dateTime={updated.iso}>{updated.label}</time>
        </p>
        <p className="mb-4 max-w-3xl bm-body-l text-bm-muted">
          The Partner API is a read-only REST API that lets approved platforms pull
          Cache athlete data and embed it in their own products, with click-through
          links back to the athlete's Cache profile. CacheScore, its contributions
          and the three indexes appear only when the athlete shares their CacheScore
          publicly. The valuation fields (CacheValue, PerformanceValue, CacheNILx
          Value, NILx Rate Card and brand alignment) are served under the partner
          agreement, within the key's plan, whatever the athlete's public-page value
          toggles say. Contact details and social media links are never shared.
        </p>
        <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
          <ValueTile label="Base URL">
            <div className="flex items-center justify-between gap-2">
              <code className="font-mono text-sm text-bm-ink">{PROD_BASE}</code>
              <CopyButton text={PROD_BASE} label="Base URL" ariaLabel="Copy base URL" />
            </div>
          </ValueTile>
          <ValueTile label="Format">
            <div className="bm-body-m text-bm-ink">JSON over HTTPS. All endpoints are <span className="font-semibold">GET</span> only.</div>
          </ValueTile>
        </div>
      </section>

      {/* Authentication */}
      <section id="authentication">
        <SectionTitle overline="Access">Authentication</SectionTitle>
        <p className="mb-4 max-w-3xl bm-body-l text-bm-muted">
          Every request carries an API key issued by the Cache team. The key is
          sent on every request in the{' '}
          <InlineCode>X-API-Key</InlineCode> header. The alternative form{' '}
          <InlineCode>Authorization: ApiKey &lt;key&gt;</InlineCode> is also accepted.
        </p>
        <CodeBlock
          language="curl"
          label="Auth example"
          code={`curl -H "X-API-Key: cache_XXXXXXXXXXXX" ${PROD_BASE}/api/partner/v1/ping/`}
        />
        <Note>
          Keys are shown once at creation and stored hashed. Disabling a key pauses
          access and can be undone. Revoking is permanent.
        </Note>
      </section>

      {/* Plans and credits */}
      <PlansSection docs={plans} />

      {/* Endpoints */}
      <section id="endpoints">
        <SectionTitle overline="Reference">Endpoints</SectionTitle>

        {/* Ping */}
        <Endpoint
          className="mb-6"
          path="/api/partner/v1/ping/"
          copyUrl={`${PROD_BASE}/api/partner/v1/ping/`}
          summary="Key check and quota info"
        >
          <p className="bm-body-m text-bm-muted">
            Verifies the key and returns its label, platform, rate limit, plan,
            credits left, and the endpoints and field groups it unlocks. Ping uses
            no credits, so use it as a health check when wiring up an integration.
          </p>
          <CodeBlock
            language="200 response"
            label="Ping response"
            code={`{
  "ok": true,
  "keyName": "Shoot 360 production",
  "platform": "shoot360",
  "rateLimitPerMin": 120,
  "endpoints": ["/api/partner/v1/athletes/", "/api/partner/v1/athletes/<athleteId>/"],
  "plan": "performance",
  "planLabel": "Performance",
  "planStatus": "active",
  "periodEnd": "2026-11-19T12:00:00Z",
  "credits": {
    "unlimited": false, "monthlyRemaining": 940, "monthlyAllowance": 1000,
    "purchased": 500, "remaining": 1440
  },
  "creditCosts": { "detail": 1, "list": 1, "ping": 0 },
  "fieldGroups": ["profile", "stats", "performance"]
}`}
          />
          <PingGroupIds docs={plans} />
        </Endpoint>

        {/* Athletes list */}
        <Endpoint
          className="mb-6"
          path="/api/partner/v1/athletes/"
          copyUrl={`${PROD_BASE}/api/partner/v1/athletes/`}
          summary="Searchable, paginated athlete index"
        >
          <p className="bm-body-m text-bm-muted">
            Returns athlete cards for every athlete with a profile photo who has not
            opted out of the public index. CacheScore appears only when the athlete
            allows it. Each card includes <InlineCode>profileUrl</InlineCode>, the
            click-through link to the athlete's Cache profile.
          </p>

          <div>
            <SubLabel>Query parameters</SubLabel>
            <DocsTable
              headers={['Parameter', 'Type', 'Description']}
              rows={[
                ['search', 'string', 'Free-text search across name, surname, city, hometown, team name, and school name.'],
                ['sport', 'string', <>Sport name filter, e.g. <InlineCode>Basketball</InlineCode>. Valid names come back in the <InlineCode>sports</InlineCode> array. Omit or use <InlineCode>All Players</InlineCode> for every sport.</>],
                ['page', 'integer', 'Page number, starting at 1.'],
                ['limit', 'integer', 'Results per page. Default 24, maximum 60.'],
                ['searchFields', 'JSON string', <>URL-encoded JSON for field filters: <InlineCode>name</InlineCode>, <InlineCode>surname</InlineCode>, <InlineCode>city</InlineCode>, <InlineCode>homeTown</InlineCode>, <InlineCode>schoolName</InlineCode> (text), <InlineCode>gender</InlineCode> and <InlineCode>state</InlineCode> (numeric codes from <InlineCode>filterOptions</InlineCode>), and <InlineCode>cacheScore</InlineCode> (minimum score).</>],
              ]}
            />
          </div>

          <div>
            <SubLabel>Response fields (per athlete card)</SubLabel>
            <DocsTable
              headers={['Field', 'Type', 'Description']}
              rows={[
                ['athleteId', 'string', 'Stable Cache athlete id. Use it for the detail endpoint and profile links.'],
                ['name / surname', 'string', 'First and last name.'],
                ['avatar', 'string', 'Absolute Cloudinary image URL, hotlinkable from any origin.'],
                ['sport / level / team / school / state', 'string', 'Display names, already resolved (level is High School, College, or Pro). team is the club team (or a pro\'s team) with its age group, e.g. Oakland Soldiers 16U; school is the school the Cache profile leads with.'],
                ['hometown', 'string', 'Hometown as "City, ST", e.g. Oakland, CA; may be empty.'],
                ['jerseyNumber', 'string', 'Jersey number, may be empty.'],
                ['cacheScore', 'number', 'CacheScore 0 to 100. Present only if the athlete shares it publicly.'],
                ['cacheScoreConfidence', 'string', 'High, Medium, or Low. Present alongside cacheScore.'],
                ['performanceIndex / visibilityIndex / offCourtIndex', 'number | null', 'Performance, visibility, and off-court scores out of 10 with one decimal (7.8), the numbers the Cache profile shows; null for a score the athlete has no data for yet. Present only if the athlete shares their CacheScore publicly.'],
                ['cacheValue', 'object', 'CacheValue estimated range { min, max } in USD. Present when the NIL engine can value the athlete\'s team.'],
                ['performanceValue', 'object', 'PerformanceValue { min, max, value } in USD: the athlete\'s market value from the performance score (sport score, team, and level), with visibility and off-court estimated. min and max are its low and high scenarios, value the point estimate. Present when the athlete has a sport score and the NIL engine can value their team.'],
                ['cacheNilxValue', 'object', 'CacheNILx Value { min, max, tier } in USD; tier is Emerging, Moderate engagement, or High engagement.'],
                ['nilRateCard', 'object', 'NILx Rate Card in USD: events, socialPost, reel, story, longVideo, share. Categories below follower thresholds return null. Present when the athlete has social metrics.'],
                ['brandAlignment', 'array', 'Brand category alignment: { category, fit } for six categories, fit is Strong, Good, Moderate, or Low.'],
                ['profileUrl', 'string', 'Click-through link to the athlete\'s Cache profile.'],
              ]}
            />
          </div>

          <CodeBlock language="200 response, Full access" label="List response" code={listExample} />
          <CodeBlock language="curl, filtered" label="Filtered list example" code={searchFieldsExample} />
        </Endpoint>

        {/* Athlete detail */}
        <Endpoint
          path="/api/partner/v1/athletes/{athleteId}/"
          copyUrl={`${PROD_BASE}/api/partner/v1/athletes/{athleteId}/`}
          summary="Single athlete profile"
        >
          <p className="bm-body-m text-bm-muted">
            The profile behind the public share page plus NIL valuation data.
            CacheScore, contributions and the three indexes follow the athlete's
            cacheScorePublic toggle (the <InlineCode>privacy</InlineCode> object
            shows the athlete's toggles). The NIL fields (cacheValue,
            performanceValue, cacheNilxValue, nilRateCard, brandAlignment) do not
            follow the public-page value toggles: they are included when the
            key's plan unlocks them (see Plans and credits) and the value can be
            computed. Social media links are never shared through the Partner
            API. An athlete merged into another profile answers 301 with the
            surviving profile (see Rate limits and errors).
          </p>
          <div>
            <SubLabel>Response fields</SubLabel>
            <DocsTable
              headers={['Field', 'Type', 'Description']}
              rows={[
                ['athleteId', 'string', 'Stable Cache athlete id.'],
                ['name / surname / avatar', 'string', 'Identity and photo (absolute Cloudinary URL).'],
                ['sport / level / team / position', 'string', 'Display names, already resolved. team is the club team (or a pro\'s team) with its age group, e.g. Oakland Soldiers 16U. For a woman in a sport with separate men\'s and women\'s competitions, sport names the women\'s game as the Cache profile does (e.g. Women\'s basketball); list cards keep the sport\'s own name.'],
                ['school', 'string', 'The school the Cache profile leads with: a college athlete\'s college, a pro\'s college, else the high school. May be empty.'],
                ['college / university / highSchool / club / proTeam', 'string', 'The athlete\'s schools and teams by slot, as the Cache profile names them: college (current college), university (a pro\'s college), highSchool (current high school, else the last one), club (club team with its age group), proTeam (pro team). Empty when the athlete has none.'],
                ['collegeLevel', 'object | null', 'A college athlete\'s class { code, label, estimated }: code FR, SO, JR, SR, or GR, label Freshman, Sophomore, Junior, Senior, or Graduate; estimated is true when the class was worked out from the high school class year. null when there is none.'],
                ['jerseyNumber / location / gradYear', 'string', 'Jersey number, hometown as "City, ST" (e.g. Oakland, CA), and graduation year.'],
                ['privacy', 'object', 'The athlete\'s sharing toggles: cacheScorePublic, cacheValueVisible, nilDealsVisible. Only cacheScorePublic changes this response; the value toggles apply to the athlete\'s public page.'],
                ['cacheScore', 'number', 'Conditional on cacheScorePublic. CacheScore 0 to 100 with cacheScoreConfidence.'],
                ['contributions', 'object', 'Conditional on cacheScorePublic. Performance, visibility, and off-court scores behind the CacheScore, each 0 to 100, or null when the athlete has no data for that score yet (never a 0 it did not earn).'],
                ['performanceIndex / visibilityIndex / offCourtIndex', 'number | null', 'Conditional on cacheScorePublic. The same three scores out of 10 with one decimal (contributions / 10), the numbers the Cache profile shows; null wherever contributions is null.'],
                ['cacheValue', 'object', 'CacheValue estimated range { min, max } in USD. Present when the NIL engine can value the athlete\'s team.'],
                ['performanceValue', 'object', 'PerformanceValue { min, max, value } in USD: market value from the performance score (sport score, team, and level), with visibility and off-court estimated; min and max are its low and high scenarios. Present when the athlete has a sport score and the NIL engine can value their team.'],
                ['cacheNilxValue', 'object', 'CacheNILx Value { min, max, tier } in USD; tier is Emerging, Moderate engagement, or High engagement.'],
                ['nilRateCard', 'object', 'NILx Rate Card in USD: events, socialPost, reel, story, longVideo, share. Categories below follower thresholds return null.'],
                ['brandAlignment', 'array', 'Brand category alignment: { category, fit } for six categories, fit is Strong, Good, Moderate, or Low.'],
                ['profileUrl', 'string', 'Click-through link to the Cache profile.'],
                ['shareImageUrl', 'string', 'Ready-made 1200x630 share card PNG for rich embeds.'],
              ]}
            />
          </div>
          <CodeBlock language="200 response, Full access" label="Detail response" code={detailExample} />
          <p className="bm-body-m text-bm-muted">
            The same athlete for a key on the Performance plan: the Visibility and
            value fields (cacheValue, cacheNilxValue, nilRateCard, brandAlignment,
            contributions, visibilityIndex, offCourtIndex) are left out, and the
            response says how many credits the key has left.
          </p>
          <CodeBlock language="200 response, Performance plan" label="Performance plan response" code={performancePlanExample} />
        </Endpoint>
      </section>

      {/* Embed + click-through */}
      <section id="embedding">
        <SectionTitle overline="Integration">Embedding and click-through links</SectionTitle>
        <p className="mb-4 max-w-3xl bm-body-l text-bm-muted">
          The intended flow: the partner renders athlete cards from the API in their
          own UI, and every card links to <InlineCode>profileUrl</InlineCode>. The
          profile page carries rich Open Graph tags, so the same link unfurls into a
          branded card when shared in social apps and messengers.
        </p>
        <div className="mb-4 grid max-w-4xl gap-3 md:grid-cols-2">
          <ValueTile label="Profile link format" icon="link">
            <div className="flex items-center justify-between gap-2">
              <code className="break-all font-mono text-xs text-bm-ink">{PROD_BASE}/p/&#123;athleteId&#125;</code>
              <CopyButton text={`${PROD_BASE}/p/{athleteId}`} label="Profile link format" ariaLabel="Copy profile link format" />
            </div>
          </ValueTile>
          <ValueTile label="Share card image" icon="image">
            <div className="flex items-center justify-between gap-2">
              <code className="break-all font-mono text-xs text-bm-ink">{PROD_BASE}/api/users/public/athlete/&#123;athleteId&#125;/preview.png</code>
              <CopyButton text={`${PROD_BASE}/api/users/public/athlete/{athleteId}/preview.png`} label="Share image format" ariaLabel="Copy share image format" />
            </div>
          </ValueTile>
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <CodeBlock language="JavaScript" label="JavaScript example" code={jsExample} />
          <CodeBlock language="Python" label="Python example" code={pyExample} />
        </div>
        <Note>
          Calling the API from a browser instead of a server? Add the partner's site
          origins to the key's allowed origins and CORS is handled automatically.
          Avatar images load from Cloudinary and need no CORS setup.
        </Note>
      </section>

      {/* Rate limits + errors */}
      <section id="rate-limits">
        <SectionTitle overline="Operations">Rate limits and errors</SectionTitle>
        <p className="mb-4 max-w-3xl bm-body-l text-bm-muted">
          Each key has its own requests-per-minute limit, set when the key is created
          and editable any time. Exceeding it returns <InlineCode>429</InlineCode>{' '}
          with a <InlineCode>Retry-After</InlineCode> header, the seconds until the
          minute window resets.
        </p>
        <DocsTable
          headers={['Status', 'Meaning', 'Typical cause']}
          rows={[
            ['200', 'Success.', 'Request served.'],
            ['301', 'Moved permanently.', <>The athlete was merged into another profile. The <InlineCode>Location</InlineCode> header points to the same endpoint for the surviving profile, which the body names. Not billed; following it is a new detail request, billed like any other.</>],
            ['401', 'Not authenticated.', 'Missing, invalid, disabled, or revoked key, or a browser origin the key does not allow.'],
            ['402', 'Out of credits.', 'A plan key has used its monthly and purchased credits. Buy a credit package or wait for the monthly allowance to renew.'],
            ['404', 'Not found.', 'Unknown athleteId on the detail endpoint, or a merged athlete whose surviving profile no longer exists.'],
            ['429', 'Rate limited.', 'The key exceeded its requests-per-minute limit. Wait the seconds in the Retry-After header, then retry.'],
            ['500', 'Server error.', 'Unexpected failure on our side. Safe to retry with backoff.'],
          ]}
        />
        <div className="mt-4">
          <CodeBlock language="301 response" label="Merged athlete response" code={mergedExample} />
        </div>
        {/* The headers the server documents (api_plans PARTNER_PLAN_DOCS);
            without them, what the credit header says on a 402. */}
        {headers.length > 0 ? (
          <div className="mt-4">
            <SubLabel>Response headers</SubLabel>
            <DocsTable headers={['Header', 'Meaning']} rows={headers.map((h) => [h.name, h.description || ''])} />
          </div>
        ) : (
          <Note>
            Only 200 responses use a plan key's credits; a 301 or an error never
            does. A <InlineCode>402</InlineCode> sent before a request runs
            carries no <InlineCode>X-Credits-Remaining</InlineCode>; one sent
            because another request spent the last credit first carries{' '}
            <InlineCode>X-Credits-Remaining: 0</InlineCode>.
          </Note>
        )}
      </section>
    </div>
  );
};

export default PartnerApiDocs;
